"use client";

import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { createLogAdapter } from "@/lib/logger/console-adapter";

export type FeatureGuideId = "view-mode-toggle" | "banner-install-app";

interface GuideState {
	dismissed: boolean;
	impressions: number;
	lastShownAt: number | null;
}

interface GuideConfig {
	priority: number;
	maxImpressions: number;
	cooldownMs: number;
}

type GuideStateMap = Record<FeatureGuideId, GuideState>;
type GuideCandidateMap = Partial<Record<FeatureGuideId, boolean>>;

interface FeatureGuideContextType {
	activeGuideId: FeatureGuideId | null;
	isGuideActive: (guideId: FeatureGuideId) => boolean;
	dismissGuide: (guideId: FeatureGuideId) => void;
	registerCandidate: (guideId: FeatureGuideId, enabled: boolean) => void;
}

const STORAGE_KEY = "odin_feature_guides_v1";
const uiLog = createLogAdapter("contexts/feature-guide-context.tsx");

const GUIDE_CONFIG: Record<FeatureGuideId, GuideConfig> = {
	"view-mode-toggle": {
		priority: 20,
		maxImpressions: 3,
		cooldownMs: 24 * 60 * 60 * 1000,
	},
	"banner-install-app": {
		priority: 10,
		maxImpressions: 3,
		cooldownMs: 24 * 60 * 60 * 1000,
	},
};

const GUIDE_IDS = Object.keys(GUIDE_CONFIG) as FeatureGuideId[];

const createDefaultState = (): GuideStateMap => ({
	"view-mode-toggle": { dismissed: false, impressions: 0, lastShownAt: null },
	"banner-install-app": { dismissed: false, impressions: 0, lastShownAt: null },
});

const FeatureGuideContext = createContext<FeatureGuideContextType | undefined>(
	undefined,
);

export function FeatureGuideProvider({ children }: { children: ReactNode }) {
	const [isHydrated, setIsHydrated] = useState(false);
	const [guideState, setGuideState] = useState<GuideStateMap>(createDefaultState);
	const [candidates, setCandidates] = useState<GuideCandidateMap>({});
	const [activeGuideId, setActiveGuideId] = useState<FeatureGuideId | null>(null);

	useEffect(() => {
		try {
			const raw = localStorage.getItem(STORAGE_KEY);
			if (!raw) {
				setIsHydrated(true);
				return;
			}

			const parsed = JSON.parse(raw) as Partial<GuideStateMap>;
			setGuideState({
				...createDefaultState(),
				...parsed,
			});
		} catch (error) {
			uiLog.error("Failed to load feature guide state:", error);
		} finally {
			setIsHydrated(true);
		}
	}, []);

	useEffect(() => {
		if (!isHydrated) return;
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(guideState));
		} catch (error) {
			uiLog.error("Failed to persist feature guide state:", error);
		}
	}, [guideState, isHydrated]);

	useEffect(() => {
		if (!isHydrated) return;

		const now = Date.now();
		if (activeGuideId) {
			const activeState = guideState[activeGuideId];
			const activeConfig = GUIDE_CONFIG[activeGuideId];
			const canKeepActive =
				!!candidates[activeGuideId] &&
				!!activeState &&
				!activeState.dismissed &&
				activeState.impressions <= activeConfig.maxImpressions;

			// Keep current guide mounted while it remains eligible.
			// Cooldown is used for re-show decisions, not for immediate active visibility.
			if (canKeepActive) return;
		}

		const nextActive = GUIDE_IDS
			.filter((guideId) => candidates[guideId])
			.sort((a, b) => GUIDE_CONFIG[a].priority - GUIDE_CONFIG[b].priority)
			.find((guideId) => {
				const state = guideState[guideId];
				const config = GUIDE_CONFIG[guideId];
				if (!state || state.dismissed) return false;
				if (state.impressions >= config.maxImpressions) return false;

				if (!state.lastShownAt) return true;
				return now - state.lastShownAt >= config.cooldownMs;
			});

		setActiveGuideId(nextActive ?? null);
	}, [candidates, guideState, isHydrated]);

	useEffect(() => {
		if (!activeGuideId || !isHydrated) return;

		setGuideState((prev) => {
			const current = prev[activeGuideId];
			const now = Date.now();
			if (
				current.lastShownAt &&
				now - current.lastShownAt < 1500
			) {
				return prev;
			}

			return {
				...prev,
				[activeGuideId]: {
					...current,
					impressions: current.impressions + 1,
					lastShownAt: now,
				},
			};
		});
	}, [activeGuideId, isHydrated]);

	const registerCandidate = useCallback(
		(guideId: FeatureGuideId, enabled: boolean) => {
			setCandidates((prev) => {
				if (prev[guideId] === enabled) return prev;
				return { ...prev, [guideId]: enabled };
			});
		},
		[],
	);

	const dismissGuide = useCallback((guideId: FeatureGuideId) => {
		setGuideState((prev) => ({
			...prev,
			[guideId]: {
				...prev[guideId],
				dismissed: true,
			},
		}));
		setActiveGuideId((prev) => (prev === guideId ? null : prev));
	}, []);

	const isGuideActive = useCallback(
		(guideId: FeatureGuideId) => activeGuideId === guideId,
		[activeGuideId],
	);

	const value = useMemo(
		() => ({
			activeGuideId,
			isGuideActive,
			dismissGuide,
			registerCandidate,
		}),
		[activeGuideId, dismissGuide, isGuideActive, registerCandidate],
	);

	return (
		<FeatureGuideContext.Provider value={value}>
			{children}
		</FeatureGuideContext.Provider>
	);
}

export function useFeatureGuide() {
	const context = useContext(FeatureGuideContext);
	if (!context) {
		throw new Error("useFeatureGuide must be used within a FeatureGuideProvider");
	}
	return context;
}

export function useFeatureGuideCandidate(
	guideId: FeatureGuideId,
	enabled: boolean,
) {
	const { registerCandidate } = useFeatureGuide();

	useEffect(() => {
		registerCandidate(guideId, enabled);
		return () => registerCandidate(guideId, false);
	}, [enabled, guideId, registerCandidate]);
}
